import React, { useEffect, useState, useCallback } from "react";
import {
  Star,
  StarHalf,
  RefreshCw,
  Search,
  Calendar,
  Filter,
  Download,
  Eye,
  X,
  Stethoscope,
  Pill,
  FlaskConical,
  Building2,
  BookOpen,
  CalendarCheck,
  User,
  Phone,
  MessageSquare,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  Info,
  Smartphone,
  ShoppingBag,
} from "lucide-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  AnalyticsService,
  ReviewItem,
  ReviewAnalyticsDashboardData,
  EntityTypeBreakdown,
} from "../../services/api/analytics.service";
import { AnalyticsSubTabs } from "./AnalyticsSubTabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";

const ENTITY_COLORS: Record<string, string> = {
  DOCTOR: "#10B981", // Emerald
  APPOINTMENT: "#3B82F6", // Blue
  MEDICINE_ORDER: "#F59E0B", // Amber
  PRODUCT: "#F59E0B", // Amber
  APPLICATION: "#6366F1", // Indigo
  APP: "#6366F1", // Indigo
  LABTEST: "#8B5CF6", // Purple
  CLINIC: "#06B6D4", // Cyan
  BLOG: "#EC4899", // Pink
  OTHER: "#6B7280", // Gray
};

export function ReviewAnalyticsView() {
  const [loadingDashboard, setLoadingDashboard] = useState<boolean>(true);
  const [loadingListing, setLoadingListing] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<ReviewAnalyticsDashboardData | null>(null);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [selectedReview, setSelectedReview] = useState<ReviewItem | null>(null);

  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("ALL");
  const [ratingFilter, setRatingFilter] = useState<string>("ALL");
  const [searchText, setSearchText] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Fetch Dashboard Stats
  const fetchDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const res = await AnalyticsService.getReviewDashboard({
        entityType: entityTypeFilter !== "ALL" ? entityTypeFilter : undefined,
        rating: ratingFilter !== "ALL" ? Number(ratingFilter) : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (res && res.success && res.data) {
        setDashboardData(res.data);
      } else {
        // Fallback calculation or friendly toast
        if (res?.message && !res.success) {
          toast.error(res.message);
        }
      }
    } catch (err) {
      console.error("Error fetching review dashboard:", err);
      toast.error("Failed to load review analytics metrics");
    } finally {
      setLoadingDashboard(false);
    }
  }, [entityTypeFilter, ratingFilter, startDate, endDate]);

  // Fetch Listing
  const fetchListing = useCallback(async () => {
    setLoadingListing(true);
    try {
      const res = await AnalyticsService.getReviews({
        page: currentPage,
        pageSize,
        entityType: entityTypeFilter !== "ALL" ? entityTypeFilter : undefined,
        rating: ratingFilter !== "ALL" ? Number(ratingFilter) : undefined,
        searchText: searchText.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (res && res.success && Array.isArray(res.data)) {
        setReviews(res.data);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 1);
          setTotalRecords(res.pagination.total ?? res.pagination.totalData ?? res.data.length);
        } else {
          setTotalRecords(res.data.length);
          setTotalPages(Math.ceil(res.data.length / pageSize) || 1);
        }
      } else {
        setReviews([]);
        setTotalRecords(0);
        setTotalPages(1);
        if (res?.message && !res.success) {
          toast.error(res.message);
        }
      }
    } catch (err) {
      console.error("Error fetching review listing:", err);
      toast.error("Failed to load review listing");
      setReviews([]);
    } finally {
      setLoadingListing(false);
    }
  }, [currentPage, pageSize, entityTypeFilter, ratingFilter, searchText, startDate, endDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleRefresh = () => {
    fetchDashboard();
    fetchListing();
  };

  const handleResetFilters = () => {
    setEntityTypeFilter("ALL");
    setRatingFilter("ALL");
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (reviews.length === 0) {
      toast.error("No reviews available to export");
      return;
    }

    const exportRows = reviews.map((r) => ({
      "Review ID": r.review_id,
      "Patient Name": r.patientName || "Anonymous",
      "Patient Phone": r.patientPhoneNumber || "-",
      "Entity Type": r.entity_type,
      "Entity ID": r.entity_id,
      Rating: r.rating,
      Comment: r.comment || "-",
      "Created At": new Date(r.created_at).toLocaleString("en-IN"),
      "Order ID": r.order_id || "-",
      "Target Details": r.entityDetails ? JSON.stringify(r.entityDetails) : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reviews");
    XLSX.writeFile(workbook, `Review_Analytics_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Reviews exported successfully");
  };

  // Render Star Ratings
  const renderStars = (rating: number, size: "sm" | "md" | "lg" = "sm") => {
    const starSize = size === "lg" ? "w-6 h-6" : size === "md" ? "w-5 h-5" : "w-4 h-4";
    const stars = [];
    const roundedRating = Math.round(rating * 2) / 2;

    for (let i = 1; i <= 5; i++) {
      if (i <= roundedRating) {
        stars.push(
          <Star key={i} className={`${starSize} text-amber-400 fill-amber-400`} />
        );
      } else if (i - 0.5 === roundedRating) {
        stars.push(
          <StarHalf key={i} className={`${starSize} text-amber-400 fill-amber-400`} />
        );
      } else {
        stars.push(<Star key={i} className={`${starSize} text-gray-300`} />);
      }
    }
    return <div className="flex items-center gap-0.5">{stars}</div>;
  };

  // Render Entity Badge with Icon
  const renderEntityBadge = (type: string) => {
    const upper = (type || "").toUpperCase();
    let bg = "bg-gray-100 text-gray-700 border-gray-200";
    let label = upper;
    let Icon = Info;

    if (upper === "DOCTOR") {
      bg = "bg-emerald-50 text-emerald-700 border-emerald-200";
      Icon = Stethoscope;
      label = "DOCTOR";
    } else if (upper === "APPOINTMENT" || upper === "APPOINTMENTS") {
      bg = "bg-blue-50 text-blue-700 border-blue-200";
      Icon = CalendarCheck;
      label = "APPOINTMENT";
    } else if (upper === "MEDICINE_ORDER" || upper === "PRODUCT" || upper === "MEDICINE" || upper === "ORDER" || upper === "MEDICINE_ORDERS") {
      bg = "bg-amber-50 text-amber-700 border-amber-200";
      Icon = Pill;
      label = upper === "PRODUCT" ? "PRODUCT" : "MEDICINE ORDER";
    } else if (upper === "APPLICATION" || upper === "APP") {
      bg = "bg-indigo-50 text-indigo-700 border-indigo-200";
      Icon = Smartphone;
      label = "APPLICATION";
    } else if (upper === "LABTEST") {
      bg = "bg-purple-50 text-purple-700 border-purple-200";
      Icon = FlaskConical;
      label = "LAB TEST";
    } else if (upper === "CLINIC") {
      bg = "bg-cyan-50 text-cyan-700 border-cyan-200";
      Icon = Building2;
      label = "CLINIC";
    } else if (upper === "BLOG") {
      bg = "bg-pink-50 text-pink-700 border-pink-200";
      Icon = BookOpen;
      label = "BLOG";
    }

    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border ${bg}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
    );
  };

  // Render Enriched Entity Details
  const renderEntityDetails = (item: ReviewItem) => {
    const details = item.entityDetails;
    const type = (item.entity_type || "").toUpperCase();

    if (type === "APPOINTMENT" || type === "APPOINTMENTS") {
      const docName = details?.doctorName || "Doctor Consultation";
      const date = details?.appointmentDate;
      const time = details?.appointmentTime;
      const apptType = details?.appointmentType;

      return (
        <div className="space-y-1 max-w-[220px]">
          <p className="text-xs font-semibold text-gray-900 truncate">
            {docName}
          </p>
          {(date || time) && (
            <div className="flex items-center gap-1 text-[11px] text-gray-600">
              <Calendar className="w-3 h-3 text-emerald-600 shrink-0" />
              <span>
                {date} {time ? `• ${time}` : ""}
              </span>
            </div>
          )}
          {apptType && (
            <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
              {apptType}
            </span>
          )}
        </div>
      );
    }

    if (
      type === "MEDICINE_ORDER" ||
      type === "MEDICINE_ORDERS" ||
      type === "PRODUCT" ||
      type === "MEDICINE" ||
      type === "ORDER" ||
      type === "LABTEST"
    ) {
      const orderId = item.order_id || details?.orderId || details?.order_id || item.entity_id;
      const itemName = details?.name || details?.productName || details?.medicineName || details?.testName;

      return (
        <div className="space-y-0.5 max-w-[200px]">
          <div className="flex items-center gap-1 text-xs font-semibold text-gray-900">
            <span className="text-gray-500 font-normal">Order ID:</span>
            <span className="font-mono text-gray-800 truncate">{orderId || "-"}</span>
          </div>
          {itemName && (
            <p className="text-[11px] text-gray-500 truncate" title={itemName}>
              {itemName}
            </p>
          )}
        </div>
      );
    }

    if (type === "APPLICATION" || type === "APP") {
      return (
        <div className="space-y-0.5 max-w-[200px]">
          <p className="text-xs font-semibold text-indigo-900 truncate flex items-center gap-1">
            <Smartphone className="w-3.5 h-3.5 text-indigo-600" />
            {details?.platform || details?.appName || "Dandar Health App"}
          </p>
          {details?.appVersion && (
            <p className="text-[11px] text-gray-500">v{details.appVersion}</p>
          )}
          {(details?.device || details?.deviceModel) && (
            <p className="text-[11px] text-gray-400 truncate">{details.device || details.deviceModel}</p>
          )}
        </div>
      );
    }

    if (type === "CLINIC") {
      return (
        <div className="space-y-0.5 max-w-[200px]">
          <p className="text-xs font-semibold text-gray-900 truncate">
            {details?.clinicName || details?.name || "Clinic"}
          </p>
          {details?.phone && (
            <p className="text-[11px] text-gray-500 truncate">{details.phone}</p>
          )}
        </div>
      );
    }

    if (type === "BLOG") {
      return (
        <div className="flex items-center gap-2 max-w-[220px]">
          {details?.posterImage && (
            <img
              src={details.posterImage}
              alt="Blog poster"
              className="w-8 h-8 rounded-lg object-cover border border-gray-200 shrink-0"
              onError={(e) => {
                (e.target as HTMLElement).style.display = "none";
              }}
            />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-900 truncate">
              {details?.title || "Blog Post"}
            </p>
            {details?.doctorName && (
              <p className="text-[11px] text-gray-500 truncate">By {details.doctorName}</p>
            )}
          </div>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-xs text-gray-500 font-mono">
          ID: {item.entity_id || "N/A"}
        </div>
      );
    }

    return (
      <div className="text-xs text-gray-600 truncate max-w-[180px]">
        {typeof details === "string" ? details : JSON.stringify(details)}
      </div>
    );
  };

  // Prepare Rating Distribution Chart Data
  const totalReviewsCount = dashboardData?.totalReviews || 0;
  const ratingBreakdown = dashboardData?.ratingBreakdown || {};

  const ratingChartData = [5, 4, 3, 2, 1].map((star) => {
    const count = ratingBreakdown[star.toString()] || 0;
    const percentage = totalReviewsCount > 0 ? Math.round((count / totalReviewsCount) * 100) : 0;
    return {
      star: `${star} Star${star > 1 ? "s" : ""}`,
      starNum: star,
      count,
      percentage,
    };
  });

  // Prepare Entity Breakdown Chart Data (excluding DOCTOR)
  const entityBreakdownList: EntityTypeBreakdown[] = (dashboardData?.entityTypeBreakdown || []).filter(
    (item) => item.entityType?.toUpperCase() !== "DOCTOR"
  );
  const entityChartData = entityBreakdownList.map((item) => ({
    name: item.entityType,
    Reviews: item.totalReviews,
    AvgRating: item.averageRating,
    fill: ENTITY_COLORS[item.entityType.toUpperCase()] || ENTITY_COLORS.OTHER,
  }));

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-200/60 text-amber-600 flex items-center justify-center shadow-xs">
              <Star className="w-5 h-5 fill-amber-500 text-amber-500" />
            </div>
            Review Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track user feedback, multi-category ratings, and entity satisfaction metrics.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium text-xs rounded-xl shadow-xs transition-colors"
            title="Export review data to Excel"
          >
            <Download className="w-4 h-4 text-gray-500" />
            Export Excel
          </button>

          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors"
          >
            <RefreshCw
              className={`w-4 h-4 ${loadingDashboard || loadingListing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Analytics Sub-Tabs Navigation */}
      {/* <AnalyticsSubTabs activeTab="review-analytics" /> */}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Card 1: Total Reviews */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total Reviews
            </p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">
              {loadingDashboard ? "..." : dashboardData?.totalReviews ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Across all categories</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-200/60">
            <MessageSquare className="w-6 h-6" />
          </div>
        </div>

        {/* Card 2: Average Rating */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Average Rating
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <h3 className="text-2xl font-bold text-amber-600">
                {loadingDashboard
                  ? "..."
                  : Number(dashboardData?.averageRating || 0).toFixed(2)}
              </h3>
              <span className="text-xs text-gray-400 font-medium">/ 5.0</span>
            </div>
            <div className="mt-1">
              {renderStars(Number(dashboardData?.averageRating || 0), "sm")}
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-200/60">
            <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
          </div>
        </div>

        {/* Card 3: Appointment Reviews */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Appointment Reviews
            </p>
            <h3 className="text-2xl font-bold text-blue-600 mt-1">
              {loadingDashboard ? "..." : dashboardData?.appointmentReviewsCount ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Visits & Consultations</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-200/60">
            <CalendarCheck className="w-6 h-6" />
          </div>
        </div>

        {/* Card 4: Medicine Orders */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Medicine Orders
            </p>
            <h3 className="text-2xl font-bold text-amber-600 mt-1">
              {loadingDashboard
                ? "..."
                : dashboardData?.productReviewsCount ??
                dashboardData?.entityTypeBreakdown?.find(
                  (e) =>
                    e.entityType.toUpperCase() === "MEDICINE_ORDER" ||
                    e.entityType.toUpperCase() === "PRODUCT"
                )?.totalReviews ??
                0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Medicines & Healthcare</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-200/60">
            <Pill className="w-6 h-6" />
          </div>
        </div>

        {/* Card 5: Lab Test Reviews */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Lab Test Reviews
            </p>
            <h3 className="text-2xl font-bold text-purple-600 mt-1">
              {loadingDashboard
                ? "..."
                : dashboardData?.labtestReviewsCount ??
                dashboardData?.entityTypeBreakdown?.find(
                  (e) => e.entityType.toUpperCase() === "LABTEST"
                )?.totalReviews ??
                0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Diagnostics & Pathology</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 border border-purple-200/60">
            <FlaskConical className="w-6 h-6" />
          </div>
        </div>

        {/* Card 6: Application Reviews */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Application Reviews
            </p>
            <h3 className="text-2xl font-bold text-indigo-600 mt-1">
              {loadingDashboard
                ? "..."
                : dashboardData?.entityTypeBreakdown?.find(
                  (e) =>
                    e.entityType.toUpperCase() === "APPLICATION" ||
                    e.entityType.toUpperCase() === "APP"
                )?.totalReviews ??
                (dashboardData as any)?.applicationReviewsCount ??
                0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">App UX & Platform</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-200/60">
            <Smartphone className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Charts & Visual Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Rating Breakdown Distribution (5 Stars -> 1 Star) */}
        <div className="lg:col-span-5 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900">Rating Breakdown</h3>
                <p className="text-xs text-gray-500 mt-0.5">Rating scores distribution (1 to 5 Stars)</p>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 rounded-lg border border-amber-200/60 text-amber-700 text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                {Number(dashboardData?.averageRating || 0).toFixed(2)} Avg
              </div>
            </div>

            {/* Star Distribution Rows */}
            <div className="mt-5 space-y-3">
              {ratingChartData.map((item) => (
                <div
                  key={item.starNum}
                  onClick={() => {
                    setRatingFilter(item.starNum.toString());
                    setCurrentPage(1);
                  }}
                  className={`group cursor-pointer p-2 rounded-xl transition-colors ${ratingFilter === item.starNum.toString()
                    ? "bg-amber-50/80 border border-amber-200"
                    : "hover:bg-gray-50"
                    }`}
                >
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700 flex items-center gap-1">
                      {item.starNum} <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    </span>
                    <span className="text-gray-500 font-medium">
                      {item.count} reviews ({item.percentage}%)
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-amber-400 h-full rounded-full transition-all duration-500 group-hover:bg-amber-500"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>Click any rating tier to filter table</span>
            {ratingFilter !== "ALL" && (
              <button
                onClick={() => setRatingFilter("ALL")}
                className="text-emerald-600 font-semibold hover:underline"
              >
                Clear Star Filter
              </button>
            )}
          </div>
        </div>

        {/* Entity Type Breakdown Chart */}
        <div className="lg:col-span-7 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900">Reviews by Entity Category</h3>
                <p className="text-xs text-gray-500 mt-0.5">Volume & performance per service entity</p>
              </div>
            </div>

            <div className="w-full h-64 my-4">
              {loadingDashboard ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
                </div>
              ) : entityChartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No category analytics available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={entityChartData} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                    <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                    <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                    <Tooltip
                      formatter={(val: any, name: string) => [
                        name === "AvgRating" ? `${val} / 5.0` : `${val} reviews`,
                        name === "AvgRating" ? "Average Rating" : "Total Reviews",
                      ]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #E5E7EB" }}
                    />
                    <Bar dataKey="Reviews" radius={[6, 6, 0, 0]}>
                      {entityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Quick Entity Pill Legend */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            {entityBreakdownList.map((eb) => (
              <div
                key={eb.entityType}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200 text-xs"
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: ENTITY_COLORS[eb.entityType.toUpperCase()] || "#6B7280" }}
                />
                <span className="font-semibold text-gray-700">{eb.entityType}:</span>
                <span className="text-gray-500">{eb.totalReviews}</span>
                <span className="text-amber-600 font-bold ml-1">★ {eb.averageRating}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews Listing Section */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {/* Filters Header Bar */}
        <div className="p-5 border-b border-gray-100 bg-gray-50/50 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-emerald-600" />
                Customer & Patient Reviews
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Detailed enriched reviews with patient profiles and target entities
              </p>
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search comment, patient..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Controls Row */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-200/60">
            {/* Entity Type Filter Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 mr-1 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Entity:
              </span>
              {[
                { id: "ALL", label: "All Categories" },
                { id: "APPOINTMENT", label: "Appointments" },
                { id: "MEDICINE_ORDER", label: "Medicine Orders" },
                { id: "LABTEST", label: "Lab Tests" },
                { id: "APPLICATION", label: "Application" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setEntityTypeFilter(tab.id);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${entityTypeFilter === tab.id
                    ? "bg-emerald-600 text-white shadow-xs"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Rating Filter Dropdown & Date Range */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Star Rating Select */}
              <select
                value={ratingFilter}
                onChange={(e) => {
                  setRatingFilter(e.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Filter by star rating"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="ALL">All Stars (1 - 5)</option>
                <option value="5">5 Stars ★★★★★</option>
                <option value="4">4 Stars ★★★★☆</option>
                <option value="3">3 Stars ★★★☆☆</option>
                <option value="2">2 Stars ★★☆☆☆</option>
                <option value="1">1 Star ★☆☆☆☆</option>
              </select>

              {/* Start Date */}
              <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg px-2.5 py-1">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Start date filter"
                  className="text-xs text-gray-700 border-none outline-none bg-transparent"
                  title="Filter from Start Date"
                />
              </div>

              {/* End Date */}
              <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg px-2.5 py-1">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="End date filter"
                  className="text-xs text-gray-700 border-none outline-none bg-transparent"
                  title="Filter up to End Date"
                />
              </div>

              {/* Reset Filter Button */}
              {(entityTypeFilter !== "ALL" ||
                ratingFilter !== "ALL" ||
                searchText ||
                startDate ||
                endDate) && (
                  <button
                    onClick={handleResetFilters}
                    className="px-2.5 py-1 text-xs text-red-600 hover:bg-red-50 font-medium rounded-lg transition-colors"
                  >
                    Reset
                  </button>
                )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-100/70 text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
              <tr>
                <th className="px-5 py-3.5">Reviewer</th>
                <th className="px-5 py-3.5">Entity Type</th>
                <th className="px-5 py-3.5">Target Details</th>
                <th className="px-5 py-3.5">Rating</th>
                <th className="px-5 py-3.5">Feedback / Comment</th>
                <th className="px-5 py-3.5">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingListing ? (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-gray-500">
                    <div className="inline-flex items-center gap-2.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600" />
                      Loading reviews...
                    </div>
                  </td>
                </tr>
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-gray-500">
                    <div className="max-w-xs mx-auto text-center space-y-2">
                      <MessageSquare className="w-8 h-8 text-gray-300 mx-auto" />
                      <p className="font-semibold text-gray-700 text-sm">No reviews found</p>
                      <p className="text-xs text-gray-400">
                        Try adjusting your filters or date range to see results.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                reviews.map((item) => (
                  <tr key={item.review_id} className="hover:bg-gray-50/80 transition-colors">
                    {/* Reviewer / Patient Profile */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {item.patientImage ? (
                          <img
                            src={item.patientImage}
                            alt={item.patientName || "User"}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200 shrink-0"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-xs shrink-0">
                            {item.patientName ? item.patientName.charAt(0).toUpperCase() : "U"}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {item.patientName || "Anonymous User"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.patientPhoneNumber || item.user_id}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Entity Type Badge */}
                    <td className="px-5 py-4">{renderEntityBadge(item.entity_type)}</td>

                    {/* Enriched Entity Details */}
                    <td className="px-5 py-4">{renderEntityDetails(item)}</td>

                    {/* Rating */}
                    <td className="px-5 py-4">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          {renderStars(item.rating, "sm")}
                          <span className="text-xs font-bold text-gray-800">{item.rating}.0</span>
                        </div>
                        {item.rating >= 4 ? (
                          <span className="text-[10px] font-semibold text-emerald-600 flex items-center gap-0.5">
                            <CheckCircle2 className="w-2.5 h-2.5" /> Positive
                          </span>
                        ) : item.rating <= 2 ? (
                          <span className="text-[10px] font-semibold text-rose-600">Critical</span>
                        ) : (
                          <span className="text-[10px] font-semibold text-amber-600">Neutral</span>
                        )}
                      </div>
                    </td>

                    {/* Comment */}
                    <td className="px-5 py-4 max-w-xs">
                      <p
                        className="text-xs text-gray-700 italic line-clamp-2"
                        title={item.comment}
                      >
                        "{item.comment || "No written comment provided."}"
                      </p>
                    </td>

                    {/* Date & Time */}
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {new Date(item.created_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {new Date(item.created_at).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <p className="text-xs text-gray-500">
              Showing Page <span className="font-semibold text-gray-800">{currentPage}</span> of{" "}
              <span className="font-semibold text-gray-800">{totalPages}</span> ({totalRecords} total
              reviews)
            </p>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              aria-label="Records per page"
              className="text-xs bg-white border border-gray-300 rounded-md px-2 py-1 text-gray-700 focus:outline-none"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loadingListing}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages || loadingListing}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Review Details Modal */}
      {selectedReview && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                <h3 className="font-bold text-gray-900 text-base">Review Details</h3>
              </div>
              <button
                onClick={() => setSelectedReview(null)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              {/* Reviewer Profile */}
              <div className="flex items-center gap-3.5 p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                {selectedReview.patientImage ? (
                  <img
                    src={selectedReview.patientImage}
                    alt={selectedReview.patientName || "User"}
                    className="w-12 h-12 rounded-full object-cover border border-gray-200 shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-sm shrink-0">
                    {selectedReview.patientName ? selectedReview.patientName.charAt(0).toUpperCase() : "U"}
                  </div>
                )}
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">
                    {selectedReview.patientName || "Anonymous Patient"}
                  </h4>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Phone className="w-3 h-3 text-gray-400" />
                    {selectedReview.patientPhoneNumber || "No phone provided"}
                  </p>
                  <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                    User ID: {selectedReview.user_id}
                  </p>
                </div>
              </div>

              {/* Rating & Entity Overview */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                  <p className="text-xs text-gray-500 font-medium">Rating Score</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl font-bold text-amber-600">
                      {selectedReview.rating}.0
                    </span>
                    {renderStars(selectedReview.rating, "sm")}
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-xs text-gray-500 font-medium">Target Category</p>
                  <div className="mt-1">{renderEntityBadge(selectedReview.entity_type)}</div>
                </div>
              </div>

              {/* Target Entity Details Section */}
              <div className="space-y-2 p-3.5 bg-gray-50/80 rounded-xl border border-gray-100 text-xs">
                <p className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">
                  Target Entity Details
                </p>
                <div className="space-y-1.5 pt-1 text-gray-600">
                  {/* For Appointment */}
                  {(selectedReview.entity_type?.toUpperCase() === "APPOINTMENT" ||
                    selectedReview.entity_type?.toUpperCase() === "APPOINTMENTS") && (
                      <>
                        <p>
                          <span className="font-semibold text-gray-700">Doctor Name:</span>{" "}
                          <span className="text-gray-900 font-medium">
                            {selectedReview.entityDetails?.doctorName || "Doctor Consultation"}
                          </span>
                        </p>
                        {(selectedReview.entityDetails?.appointmentDate ||
                          selectedReview.entityDetails?.appointmentTime) && (
                            <p>
                              <span className="font-semibold text-gray-700">Appointment Schedule:</span>{" "}
                              <span className="text-gray-800">
                                {selectedReview.entityDetails?.appointmentDate || ""}{" "}
                                {selectedReview.entityDetails?.appointmentTime
                                  ? `• ${selectedReview.entityDetails?.appointmentTime}`
                                  : ""}
                              </span>
                            </p>
                          )}
                        {selectedReview.entityDetails?.appointmentType && (
                          <p>
                            <span className="font-semibold text-gray-700">Type:</span>{" "}
                            <span className="text-gray-800">{selectedReview.entityDetails?.appointmentType}</span>
                          </p>
                        )}
                        {selectedReview.entityDetails?.appointmentId && (
                          <p>
                            <span className="font-semibold text-gray-700">Appointment ID:</span>{" "}
                            <span className="font-mono text-gray-700">{selectedReview.entityDetails?.appointmentId}</span>
                          </p>
                        )}
                        {selectedReview.entityDetails?.dhrId && (
                          <p>
                            <span className="font-semibold text-gray-700">DHR ID:</span>{" "}
                            <span className="font-mono text-gray-700">{selectedReview.entityDetails?.dhrId}</span>
                          </p>
                        )}
                      </>
                    )}

                  {/* For Medicine Order / Labtest */}
                  {(selectedReview.entity_type?.toUpperCase() === "MEDICINE_ORDER" ||
                    selectedReview.entity_type?.toUpperCase() === "PRODUCT" ||
                    selectedReview.entity_type?.toUpperCase() === "MEDICINE" ||
                    selectedReview.entity_type?.toUpperCase() === "ORDER" ||
                    selectedReview.entity_type?.toUpperCase() === "LABTEST") && (
                      <>
                        <p>
                          <span className="font-semibold text-gray-700">Order ID:</span>{" "}
                          <span className="font-mono text-gray-900 font-semibold">
                            {selectedReview.order_id ||
                              selectedReview.entityDetails?.orderId ||
                              selectedReview.entityDetails?.order_id ||
                              selectedReview.entity_id ||
                              "-"}
                          </span>
                        </p>
                        {(selectedReview.entityDetails?.name ||
                          selectedReview.entityDetails?.productName ||
                          selectedReview.entityDetails?.medicineName ||
                          selectedReview.entityDetails?.testName) && (
                            <p>
                              <span className="font-semibold text-gray-700">Item / Test Name:</span>{" "}
                              <span className="text-gray-800">
                                {selectedReview.entityDetails?.name ||
                                  selectedReview.entityDetails?.productName ||
                                  selectedReview.entityDetails?.medicineName ||
                                  selectedReview.entityDetails?.testName}
                              </span>
                            </p>
                          )}
                      </>
                    )}

                  {/* For Application */}
                  {(selectedReview.entity_type?.toUpperCase() === "APPLICATION" ||
                    selectedReview.entity_type?.toUpperCase() === "APP") && (
                      <>
                        <p>
                          <span className="font-semibold text-gray-700">Platform:</span>{" "}
                          <span className="text-gray-800">
                            {selectedReview.entityDetails?.platform ||
                              selectedReview.entityDetails?.appName ||
                              "Dandar Health App"}
                          </span>
                        </p>
                        {selectedReview.entityDetails?.appVersion && (
                          <p>
                            <span className="font-semibold text-gray-700">App Version:</span>{" "}
                            <span className="font-mono text-gray-800">
                              v{selectedReview.entityDetails?.appVersion}
                            </span>
                          </p>
                        )}
                        {(selectedReview.entityDetails?.device ||
                          selectedReview.entityDetails?.deviceModel) && (
                            <p>
                              <span className="font-semibold text-gray-700">Device Model:</span>{" "}
                              <span className="text-gray-800">
                                {selectedReview.entityDetails?.device ||
                                  selectedReview.entityDetails?.deviceModel}
                              </span>
                            </p>
                          )}
                      </>
                    )}

                  {/* Other / fallback */}
                  {selectedReview.entityDetails &&
                    !["APPOINTMENT", "APPOINTMENTS", "MEDICINE_ORDER", "PRODUCT", "MEDICINE", "ORDER", "LABTEST", "APPLICATION", "APP"].includes(
                      selectedReview.entity_type?.toUpperCase() || ""
                    ) && (
                      <pre className="text-[11px] bg-white p-2.5 rounded-lg border border-gray-200 overflow-x-auto text-gray-800 font-mono">
                        {JSON.stringify(selectedReview.entityDetails, null, 2)}
                      </pre>
                    )}
                </div>
              </div>

              {/* Review Comment Feedback */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Review Comment & Feedback
                </p>
                <div className="p-4 bg-emerald-50/40 border border-emerald-100 rounded-xl text-xs text-gray-800 leading-relaxed italic">
                  "{selectedReview.comment || "No written feedback provided."}"
                </div>
              </div>

              {/* Timestamps */}
              <div className="flex items-center justify-between text-[11px] text-gray-400 pt-2 border-t border-gray-100">
                <span>
                  Submitted: {new Date(selectedReview.created_at).toLocaleString("en-IN")}
                </span>
                <span className="font-mono">Review #{selectedReview.review_id}</span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setSelectedReview(null)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white font-medium text-xs rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
